import { supabase } from './supabase';
import { sendGmailEmail } from './email';
import { getMeasurementPhotoUrl } from '../services/measurementPhotoService';

export interface QuoteRequest {
  id?: string;
  business_id: string;
  project_id?: string | null;
  supplier_id: string;
  item_name: string;
  description?: string | null;
  estimated_quantity: number;
  estimated_cost: number; // Can be 0 if unknown
  retail_price: number; // Can be 0 if unknown
  unit: string;
  notes?: string | null;
  measurement_id?: string | null;
  quote_request_group_id?: string | null; // Groups items sent together in the same email
  outgoing_email_message_id?: string | null; // Gmail Message-ID for matching replies
  outgoing_email_thread_id?: string | null; // Gmail Thread-ID for matching replies
  status?: string;
  created_at?: string;
  updated_at?: string;
  supplier?: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string | null;
  };
  project?: {
    id: string;
    title: string;
  };
  measurement?: {
    id: string;
    name: string;
    category?: string | null;
    length?: number | null;
    width?: number | null;
    height?: number | null;
    area?: number | null;
    unit: string;
    custom_fields?: Record<string, number | string> | null;
    notes?: string | null;
    photos?: Array<{ id: string; file_path: string; file_name: string }> | null;
  } | null;
}

export interface CreateQuoteRequestParams {
  business_id: string;
  project_id?: string | null;
  supplier_id: string;
  item_name: string;
  description?: string | null;
  estimated_quantity: number;
  estimated_cost: number;
  retail_price: number;
  unit?: string;
  notes?: string | null;
  measurement_id?: string | null;
  quote_request_group_id?: string | null; // Groups items sent together in the same email
}

/**
 * Create a quote request
 */
export async function createQuoteRequest(params: CreateQuoteRequestParams): Promise<QuoteRequest> {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    const { data, error } = await supabase
      .from('quote_requests')
      .insert({
        business_id: params.business_id,
        project_id: params.project_id || null,
        supplier_id: params.supplier_id,
        item_name: params.item_name,
        description: params.description || null,
        estimated_quantity: params.estimated_quantity,
        estimated_cost: params.estimated_cost,
        retail_price: params.retail_price,
        unit: params.unit || 'unit',
        notes: params.notes || null,
        measurement_id: params.measurement_id || null,
        quote_request_group_id: params.quote_request_group_id || null
      })
      .select()
      .single();
    
    if (error) throw error;
    if (!data) throw new Error('Failed to create quote request');
    
    return data;
  } catch (error) {
    console.error('Error creating quote request:', error);
    throw error;
  }
}

/**
 * Update a quote request (e.g., update price after receiving quote from supplier)
 */
export async function updateQuoteRequest(
  quoteRequestId: string,
  updates: {
    estimated_cost?: number;
    retail_price?: number;
    status?: string;
  }
): Promise<QuoteRequest> {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    const { data, error } = await supabase
      .from('quote_requests')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', quoteRequestId)
      .select(`
        *,
        supplier:customers(
          id,
          first_name,
          last_name,
          email
        ),
        project:projects(
          id,
          title
        ),
        measurement:project_measurements(
          id,
          name,
          category,
          length,
          width,
          height,
          area,
          unit,
          custom_fields,
          notes
        )
      `)
      .single();
    
    if (error) throw error;
    if (!data) throw new Error('Failed to update quote request');
    
    // Load photos if measurement exists
    if (data.measurement?.id) {
      try {
        const { data: photos, error: photosError } = await supabase
          .from('measurement_photos')
          .select('id, file_path, file_name')
          .eq('measurement_id', data.measurement.id)
          .order('created_at', { ascending: false });
        
        if (!photosError && photos) {
          return {
            ...data,
            measurement: {
              ...data.measurement,
              photos: photos
            }
          };
        }
      } catch (err) {
        console.error('Error loading photos for measurement:', err);
      }
    }
    
    return data;
  } catch (error) {
    console.error('Error updating quote request:', error);
    throw error;
  }
}

/**
 * Get quote requests for a business (optionally filtered by project)
 */
export async function getQuoteRequests(businessId: string, options?: { projectId?: string }) {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    let query = supabase
      .from('quote_requests')
      .select(`
        *,
        supplier:customers(
          id,
          first_name,
          last_name,
          email
        ),
        project:projects(
          id,
          title
        ),
        measurement:project_measurements(
          id,
          name,
          category,
          length,
          width,
          height,
          area,
          unit,
          custom_fields,
          notes
        )
      `)
      .eq('business_id', businessId);
    
    if (options?.projectId) {
      query = query.eq('project_id', options.projectId);
    }
    
    const { data, error } = await query
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Load photos for each measurement
    const requestsWithPhotos = await Promise.all((data || []).map(async (request) => {
      if (request.measurement?.id) {
        try {
          const { data: photos, error: photosError } = await supabase
            .from('measurement_photos')
            .select('id, file_path, file_name')
            .eq('measurement_id', request.measurement.id)
            .order('created_at', { ascending: false });
          
          if (!photosError && photos) {
            return {
              ...request,
              measurement: {
                ...request.measurement,
                photos: photos
              }
            };
          }
        } catch (err) {
          console.error('Error loading photos for measurement:', err);
        }
      }
      return request;
    }));
    
    return requestsWithPhotos;
  } catch (error) {
    console.error('Error fetching quote requests:', error);
    throw error;
  }
}

/**
 * Generate HTML email template for quote request
 */
export function generateQuoteRequestEmailHTML(
  supplierName: string,
  businessName: string,
  projectTitle: string | null,
  itemName: string,
  description: string | null,
  estimatedQuantity: number,
  estimatedCost: number,
  retailPrice: number,
  unit: string,
  notes: string | null
): string {
  const costDisplay = estimatedCost > 0 ? `$${estimatedCost.toFixed(2)}` : 'TBD';
  const priceDisplay = retailPrice > 0 ? `$${retailPrice.toFixed(2)}` : 'TBD';
  
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Quote Request: ${itemName}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #F3F4F6;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #F3F4F6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width: 600px; background-color: #FFFFFF; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          
          <!-- Header -->
          <tr>
            <td style="padding: 40px 40px 30px; text-align: center; background-color: #FFFFFF;">
              <h1 style="margin: 0; font-size: 28px; font-weight: bold; color: #111827; letter-spacing: -0.5px;">
                Quote Request
              </h1>
              <p style="margin: 12px 0 0 0; font-size: 16px; color: #6B7280;">
                ${itemName}
              </p>
            </td>
          </tr>

          <!-- Main Content -->
          <tr>
            <td style="padding: 0 40px 40px;">
              <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.6; color: #374151;">
                Hi ${supplierName || 'there'},
              </p>
              
              <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.6; color: #374151;">
                We are requesting a quote from <strong>${businessName}</strong>${projectTitle ? ` for project: ${projectTitle}` : ''}.
              </p>

              <!-- Item Details -->
              <div style="margin: 24px 0; padding: 20px; background-color: #F9FAFB; border-radius: 8px; border: 1px solid #E5E7EB;">
                <h2 style="margin: 0 0 16px; font-size: 18px; font-weight: 600; color: #111827;">
                  Item Details
                </h2>
                <table cellspacing="0" cellpadding="0" border="0" width="100%">
                  <tr>
                    <td style="padding: 8px 0; font-weight: 600; color: #374151; width: 40%;">Item Name:</td>
                    <td style="padding: 8px 0; color: #111827;">${itemName}</td>
                  </tr>
                  ${description ? `
                  <tr>
                    <td style="padding: 8px 0; font-weight: 600; color: #374151;">Description:</td>
                    <td style="padding: 8px 0; color: #111827;">${description}</td>
                  </tr>
                  ` : ''}
                  <tr>
                    <td style="padding: 8px 0; font-weight: 600; color: #374151;">Quantity:</td>
                    <td style="padding: 8px 0; color: #111827;">${estimatedQuantity} ${unit}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; font-weight: 600; color: #374151;">Estimated Cost:</td>
                    <td style="padding: 8px 0; color: #111827;">${costDisplay}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; font-weight: 600; color: #374151;">Retail Price:</td>
                    <td style="padding: 8px 0; color: #111827;">${priceDisplay}</td>
                  </tr>
                </table>
              </div>

              ${notes ? `
              <div style="margin: 24px 0; padding: 16px; background-color: #FEF3C7; border-left: 4px solid #F59E0B; border-radius: 4px;">
                <p style="margin: 0; font-size: 15px; line-height: 1.6; color: #92400E; white-space: pre-wrap;">${notes.replace(/\n/g, '<br>')}</p>
              </div>
              ` : ''}

              <p style="margin: 24px 0 0; font-size: 16px; line-height: 1.6; color: #374151;">
                Please provide a quote for this item at your earliest convenience. If you have any questions, please don't hesitate to reach out.
              </p>

              <p style="margin: 24px 0 0; font-size: 16px; line-height: 1.6; color: #374151;">
                Thank you,<br>
                <strong>${businessName}</strong>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `;
}

/**
 * Send quote request email to supplier
 */
export async function sendQuoteRequestEmail(
  businessId: string,
  supplierEmail: string,
  supplierName: string,
  businessName: string,
  projectTitle: string | null,
  itemName: string,
  description: string | null,
  estimatedQuantity: number,
  estimatedCost: number,
  retailPrice: number,
  unit: string,
  notes: string | null
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supplierEmail) {
      return { success: false, error: 'Supplier email is required' };
    }

    const subject = `Quote Request: ${itemName}${projectTitle ? ` - ${projectTitle}` : ''}`;
    const htmlBody = generateQuoteRequestEmailHTML(
      supplierName,
      businessName,
      projectTitle,
      itemName,
      description,
      estimatedQuantity,
      estimatedCost,
      retailPrice,
      unit,
      notes
    );

    const result = await sendGmailEmail({
      businessId,
      to: supplierEmail,
      subject,
      body: htmlBody
    });

    return result;
  } catch (error) {
    console.error('Error sending quote request email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send email'
    };
  }
}

interface QuoteItemForEmail {
  name: string;
  description: string | null;
  quantity: number;
  estimatedCost: number;
  retailPrice: number;
  unit: string;
  supplierId?: string; // Add supplier ID for linking
  measurement?: {
    name: string;
    length?: number | null;
    width?: number | null;
    height?: number | null;
    area?: number | null;
    unit: string;
    custom_fields?: Record<string, number | string> | null;
    field_units?: Record<string, string> | null;
    photos?: Array<{ file_path: string; file_name: string }> | null;
  } | null;
}

/**
 * Generate HTML email body for quote request with multiple items
 */
export function generateQuoteRequestEmailHTMLForMultipleItems(
  supplierName: string,
  businessName: string,
  projectTitle: string | null,
  items: QuoteItemForEmail[],
  notes: string | null
): string {
  const itemsHtml = items.map(item => {
    const costDisplay = item.estimatedCost > 0 ? `$${item.estimatedCost.toFixed(2)}` : 'TBD';
    const priceDisplay = item.retailPrice > 0 ? `$${item.retailPrice.toFixed(2)}` : 'TBD';
    
    const measurementHtml = item.measurement ? `
      <tr>
        <td colspan="2" style="padding: 12px 0;">
          <div style="background-color: #EFF6FF; border-left: 3px solid #3B82F6; padding: 12px; border-radius: 4px; margin-top: 8px;">
            <div style="font-weight: 600; color: #1E40AF; margin-bottom: 6px; font-size: 14px;">
              Measurement: ${item.measurement.name}
            </div>
            <table cellspacing="0" cellpadding="0" border="0" width="100%" style="font-size: 13px;">
            ${item.measurement.length ? `
              <tr>
                <td style="padding: 2px 0; color: #1E40AF; width: 40%;">Length:</td>
              <td style="padding: 2px 0; color: #1E3A8A;">${item.measurement.length} ${item.measurement.field_units?.length || item.measurement.unit}</td>
              </tr>
              ` : ''}
              ${item.measurement.width ? `
              <tr>
                <td style="padding: 2px 0; color: #1E40AF;">Width:</td>
              <td style="padding: 2px 0; color: #1E3A8A;">${item.measurement.width} ${item.measurement.field_units?.width || item.measurement.unit}</td>
              </tr>
              ` : ''}
              ${item.measurement.height ? `
              <tr>
                <td style="padding: 2px 0; color: #1E40AF;">Height:</td>
              <td style="padding: 2px 0; color: #1E3A8A;">${item.measurement.height} ${item.measurement.field_units?.height || item.measurement.unit}</td>
              </tr>
              ` : ''}
              ${item.measurement.area ? `
              <tr>
                <td style="padding: 2px 0; color: #1E40AF;">Area:</td>
              <td style="padding: 2px 0; color: #1E3A8A;">${item.measurement.area} ${(item.measurement.field_units?.area || item.measurement.unit)}²</td>
              </tr>
              ` : ''}
              ${item.measurement.custom_fields && Object.keys(item.measurement.custom_fields).length > 0 ? Object.entries(item.measurement.custom_fields).map(([key, value]) => `
              <tr>
                <td style="padding: 2px 0; color: #1E40AF; text-transform: capitalize;">${key}:</td>
              <td style="padding: 2px 0; color: #1E3A8A;">${value}${item.measurement.field_units?.[key] ? ` ${item.measurement.field_units?.[key]}` : ''}</td>
              </tr>
              `).join('') : ''}
            </table>
            ${item.measurement.photos && item.measurement.photos.length > 0 ? `
            <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #DBEAFE;">
              <div style="font-size: 12px; font-weight: 600; color: #1E40AF; margin-bottom: 8px;">
                Photos (${item.measurement.photos.length})
              </div>
              <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                ${item.measurement.photos.map((photo, index) => {
                  // Get public URL for the photo using the service function
                  const photoUrl = getMeasurementPhotoUrl(photo.file_path);
                  return `
                    <a href="${photoUrl}" target="_blank" download="${photo.file_name || `photo-${index + 1}.jpg`}" style="display: inline-block; text-decoration: none;">
                      <div style="width: 80px; height: 80px; border-radius: 4px; overflow: hidden; border: 1px solid #DBEAFE; background-color: #F3F4F6; cursor: pointer; transition: transform 0.2s;">
                        <img src="${photoUrl}" alt="${photo.file_name || `Photo ${index + 1}`}" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
                      </div>
                    </a>
                  `;
                }).join('')}
              </div>
            </div>
            ` : ''}
          </div>
        </td>
      </tr>
    ` : '';
    
    return `
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid #E5E7EB;">
          <div style="margin-bottom: 8px;">
            <strong style="font-size: 16px; color: #111827;">${item.name}</strong>
          </div>
          ${item.description ? `<div style="margin-bottom: 8px; font-size: 14px; color: #6B7280;">${item.description}</div>` : ''}
          <table cellspacing="0" cellpadding="0" border="0" width="100%" style="font-size: 14px;">
            <tr>
              <td style="padding: 4px 0; color: #374151; width: 40%;">Quantity:</td>
              <td style="padding: 4px 0; color: #111827;">${item.quantity} ${item.unit}</td>
            </tr>
            <tr>
              <td style="padding: 4px 0; color: #374151;">Estimated Cost:</td>
              <td style="padding: 4px 0; color: #111827;">${costDisplay}</td>
            </tr>
            <tr>
              <td style="padding: 4px 0; color: #374151;">Retail Price:</td>
              <td style="padding: 4px 0; color: #111827;">${priceDisplay}</td>
            </tr>
          </table>
          ${measurementHtml}
        </td>
      </tr>
    `;
  }).join('');

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Quote Request: ${items.length} Item${items.length > 1 ? 's' : ''}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #F3F4F6;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #F3F4F6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width: 600px; background-color: #FFFFFF; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          
          <!-- Header -->
          <tr>
            <td style="padding: 40px 40px 30px; text-align: center; background-color: #FFFFFF;">
              <h1 style="margin: 0; font-size: 28px; font-weight: bold; color: #111827; letter-spacing: -0.5px;">
                Quote Request
              </h1>
              <p style="margin: 12px 0 0 0; font-size: 16px; color: #6B7280;">
                ${items.length} Item${items.length > 1 ? 's' : ''}
              </p>
            </td>
          </tr>

          <!-- Main Content -->
          <tr>
            <td style="padding: 0 40px 40px;">
              <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.6; color: #374151;">
                Hi ${supplierName || 'there'},
              </p>
              
              <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.6; color: #374151;">
                We are requesting a quote from <strong>${businessName}</strong>${projectTitle ? ` for project: ${projectTitle}` : ''}.
              </p>

              <!-- Items Details -->
              <div style="margin: 24px 0; padding: 20px; background-color: #F9FAFB; border-radius: 8px; border: 1px solid #E5E7EB;">
                <h2 style="margin: 0 0 16px; font-size: 18px; font-weight: 600; color: #111827;">
                  Items Requested
                </h2>
                <table cellspacing="0" cellpadding="0" border="0" width="100%">
                  ${itemsHtml}
                </table>
              </div>

              ${notes ? `
              <div style="margin: 24px 0; padding: 16px; background-color: #FEF3C7; border-left: 4px solid #F59E0B; border-radius: 4px;">
                <p style="margin: 0; font-size: 15px; line-height: 1.6; color: #92400E; white-space: pre-wrap;">${notes.replace(/\n/g, '<br>')}</p>
              </div>
              ` : ''}

              <p style="margin: 24px 0 0; font-size: 16px; line-height: 1.6; color: #374151;">
                Please provide a quote for these items at your earliest convenience. If you have any questions, please don't hesitate to reach out.
              </p>

              <p style="margin: 24px 0 0; font-size: 16px; line-height: 1.6; color: #374151;">
                Thank you,<br>
                <strong>${businessName}</strong>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `;
}

/**
 * Fetch photo from Supabase storage and convert to base64
 */
async function fetchPhotoAsBase64(filePath: string): Promise<{ base64: string; mimeType: string; filename: string } | null> {
  try {
    if (!supabase) return null;

    // Download the file from storage
    const { data, error } = await supabase.storage
      .from('user-uploads')
      .download(filePath);

    if (error || !data) {
      console.error('Error downloading photo:', error);
      return null;
    }

    // Convert blob to base64
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onloadend = () => {
        const base64String = reader.result as string;
        // Remove data:image/...;base64, prefix
        const base64Content = base64String.split(',')[1];
        resolve(base64Content);
      };
      reader.onerror = reject;
    });
    reader.readAsDataURL(data);

    const base64Content = await base64Promise;
    const filename = filePath.split('/').pop() || 'photo.jpg';
    const mimeType = data.type || 'image/jpeg';

    return {
      base64: base64Content,
      mimeType,
      filename
    };
  } catch (error) {
    console.error('Error converting photo to base64:', error);
    return null;
  }
}

/**
 * Link a sent email to a quote request group
 * Stores email in quote_request_email_threads table (doesn't modify all_messages)
 */
export async function linkEmailToQuoteRequestGroup(
  businessId: string,
  groupId: string,
  messageId: string,
  threadId: string | undefined,
  supplierEmail: string,
  subject: string,
  content: string,
  supplierId: string
): Promise<void> {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    // Update all quote requests in the group with email IDs
    const { error: updateError } = await supabase
      .from('quote_requests')
      .update({
        outgoing_email_message_id: messageId,
        outgoing_email_thread_id: threadId || null
      })
      .eq('quote_request_group_id', groupId)
      .eq('business_id', businessId);
    
    if (updateError) {
      console.error('Error updating quote requests with email IDs:', updateError);
    }

    // Get all_messages ID for reference (read-only, don't modify)
    const { data: allMessagesRecord } = await supabase
      .from('all_messages')
      .select('id')
      .eq('business_id', businessId)
      .eq('direction', 'outbound')
      .eq('message_type', 'email')
      .eq('external_id', messageId)
      .limit(1)
      .single();

    // Store email thread entry in our separate table (doesn't touch all_messages)
    const { error: insertError } = await supabase
      .from('quote_request_email_threads')
      .insert({
        quote_request_group_id: groupId,
        business_id: businessId,
        supplier_id: supplierId,
        all_messages_id: allMessagesRecord?.id || null,
        external_id: messageId,
        email_address: supplierEmail,
        direction: 'outbound',
        subject: subject,
        content: content,
        body_html: content, // Assuming content is HTML
        sent_at: new Date().toISOString(),
        created_at: new Date().toISOString()
      });
    
    if (insertError) {
      console.error('Error storing email thread entry:', insertError);
    } else {
      console.log('✅ Stored email thread entry for quote request group:', groupId);
    }
  } catch (error) {
    console.error('Error linking email to quote request group:', error);
  }
}

/**
 * Send quote request email to supplier with multiple items
 */
export async function sendQuoteRequestEmailForMultipleItems(
  businessId: string,
  supplierEmail: string,
  supplierName: string,
  businessName: string,
  projectTitle: string | null,
  items: QuoteItemForEmail[],
  notes: string | null,
  quoteRequestGroupId?: string | null
): Promise<{ success: boolean; error?: string; messageId?: string; threadId?: string }> {
  try {
    if (!supplierEmail) {
      return { success: false, error: 'Supplier email is required' };
    }

    if (items.length === 0) {
      return { success: false, error: 'At least one item is required' };
    }

    // Include quote request group ID in subject for matching replies
    const subjectBase = `Quote Request: ${items.length} Item${items.length > 1 ? 's' : ''}${projectTitle ? ` - ${projectTitle}` : ''}`;
    const subject = quoteRequestGroupId 
      ? `${subjectBase} [QUOTE-${quoteRequestGroupId}]`
      : subjectBase;
    
    const htmlBody = generateQuoteRequestEmailHTMLForMultipleItems(
      supplierName,
      businessName,
      projectTitle,
      items,
      notes
    );

    // Collect all photos from all items to attach
    const attachments: Array<{ filename: string; content: string; type: string }> = [];
    const photoPaths = new Set<string>(); // Track unique photo paths to avoid duplicates

    for (const item of items) {
      if (item.measurement?.photos && item.measurement.photos.length > 0) {
        for (const photo of item.measurement.photos) {
          if (!photoPaths.has(photo.file_path)) {
            photoPaths.add(photo.file_path);
            const photoData = await fetchPhotoAsBase64(photo.file_path);
            if (photoData) {
              attachments.push({
                filename: photoData.filename,
                content: photoData.base64,
                type: photoData.mimeType
              });
            }
          }
        }
      }
    }

    const result = await sendGmailEmail({
      businessId,
      to: supplierEmail,
      subject,
      body: htmlBody,
      attachments: attachments.length > 0 ? attachments : undefined
    });

    // Link the email to the quote request group if groupId provided
    if (result.success && quoteRequestGroupId && result.messageId) {
      // Get supplier ID from the first item (all items in group have same supplier)
      const supplierId = items[0]?.supplierId;
      if (supplierId) {
        await linkEmailToQuoteRequestGroup(
          businessId,
          quoteRequestGroupId,
          result.messageId,
          result.threadId,
          supplierEmail,
          subject,
          htmlBody,
          supplierId
        );
      }
    }

    return result;
  } catch (error) {
    console.error('Error sending quote request email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send email'
    };
  }
}

/**
 * Match an incoming email to a quote request and store it in quote_request_email_threads
 * Does NOT modify all_messages table
 */
export async function matchIncomingEmailToQuoteRequest(
  businessId: string,
  fromEmail: string,
  inReplyTo: string | null,
  references: string | null,
  subject: string | null,
  gmailMessageId: string,
  content: string,
  bodyHtml: string | null
): Promise<void> {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    let matchedGroupId: string | null = null;
    let supplierId: string | null = null;

    // Method 1: Match by In-Reply-To header (most reliable)
    if (inReplyTo) {
      const { data: quoteRequest } = await supabase
        .from('quote_requests')
        .select('quote_request_group_id, supplier_id')
        .eq('business_id', businessId)
        .eq('outgoing_email_message_id', inReplyTo)
        .limit(1)
        .single();
      
      if (quoteRequest?.quote_request_group_id) {
        matchedGroupId = quoteRequest.quote_request_group_id;
        supplierId = quoteRequest.supplier_id;
      }
    }

    // Method 2: Match by References header (thread ID)
    if (!matchedGroupId && references) {
      // References can contain multiple IDs, check each one
      const referenceIds = references.split(/\s+/).filter(id => id.length > 0);
      for (const refId of referenceIds) {
        const { data: quoteRequest } = await supabase
          .from('quote_requests')
          .select('quote_request_group_id, supplier_id')
          .eq('business_id', businessId)
          .or(`outgoing_email_message_id.eq.${refId},outgoing_email_thread_id.eq.${refId}`)
          .limit(1)
          .single();
        
        if (quoteRequest?.quote_request_group_id) {
          matchedGroupId = quoteRequest.quote_request_group_id;
          supplierId = quoteRequest.supplier_id;
          break;
        }
      }
    }

    // Method 3: Match by subject tag [QUOTE-{id}]
    if (!matchedGroupId && subject) {
      const subjectMatch = subject.match(/\[QUOTE-([a-f0-9-]{36})\]/i);
      if (subjectMatch && subjectMatch[1]) {
        const { data: quoteRequest } = await supabase
          .from('quote_requests')
          .select('quote_request_group_id, supplier_id')
          .eq('business_id', businessId)
          .eq('quote_request_group_id', subjectMatch[1])
          .limit(1)
          .single();
        
        if (quoteRequest?.quote_request_group_id) {
          matchedGroupId = quoteRequest.quote_request_group_id;
          supplierId = quoteRequest.supplier_id;
        }
      }
    }

    // If we found a match, store the email in quote_request_email_threads
    if (matchedGroupId && supplierId) {
      // Get all_messages ID for reference (read-only, don't modify)
      const { data: allMessagesRecord } = await supabase
        .from('all_messages')
        .select('id')
        .eq('business_id', businessId)
        .eq('external_id', gmailMessageId)
        .limit(1)
        .single();

      // Store email thread entry in our separate table
      const { error: insertError } = await supabase
        .from('quote_request_email_threads')
        .insert({
          quote_request_group_id: matchedGroupId,
          business_id: businessId,
          supplier_id: supplierId,
          all_messages_id: allMessagesRecord?.id || null,
          external_id: gmailMessageId,
          email_address: fromEmail,
          direction: 'inbound',
          subject: subject || null,
          content: content,
          body_html: bodyHtml || content,
          received_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        });
      
      if (insertError) {
        console.error('Error storing incoming email thread entry:', insertError);
      } else {
        console.log('✅ Stored incoming email thread entry for quote request group:', matchedGroupId);
      }
    }
  } catch (error) {
    console.error('Error matching incoming email to quote request:', error);
  }
}

/**
 * Get email thread for a quote request group
 */
export async function getQuoteRequestEmailThread(
  businessId: string,
  groupId: string
): Promise<Array<{
  id: string;
  direction: 'outbound' | 'inbound';
  subject: string | null;
  content: string;
  body_html: string | null;
  email_address: string;
  sent_at: string | null;
  received_at: string | null;
  created_at: string;
  external_id?: string | null;
  read?: 'read' | 'unread' | null;
  all_messages_id?: string | null;
}>> {
  if (!supabase) throw new Error('Database connection not available');

  try {
    // First, get the email thread data
    const { data: threadData, error: threadError } = await supabase
      .from('quote_request_email_threads')
      .select('id, direction, subject, content, body_html, email_address, sent_at, received_at, created_at, external_id, all_messages_id')
      .eq('business_id', businessId)
      .eq('quote_request_group_id', groupId)
      .order('created_at', { ascending: true }); // Oldest first for thread display
    
    if (threadError) throw threadError;
    if (!threadData || threadData.length === 0) return [];
    
    // Get all_messages_id values that are not null
    const allMessagesIds = threadData
      .map(email => email.all_messages_id)
      .filter((id): id is string => id !== null);
    
    // Get external_id values for emails without all_messages_id
    const externalIds = threadData
      .filter(email => !email.all_messages_id && email.external_id)
      .map(email => email.external_id as string);
    
    // Fetch read status from all_messages by ID
    let readStatusMapById: Record<string, 'read' | 'unread'> = {};
    if (allMessagesIds.length > 0) {
      const { data: messagesData, error: messagesError } = await supabase
        .from('all_messages')
        .select('id, read')
        .in('id', allMessagesIds);
      
      if (!messagesError && messagesData) {
        readStatusMapById = messagesData.reduce((acc, msg) => {
          acc[msg.id] = msg.read || 'unread';
          return acc;
        }, {} as Record<string, 'read' | 'unread'>);
      }
    }
    
    // Fetch read status from all_messages by external_id (gmail_id stored in twilio_message_sid)
    let readStatusMapByExternalId: Record<string, 'read' | 'unread'> = {};
    if (externalIds.length > 0) {
      const { data: messagesData, error: messagesError } = await supabase
        .from('all_messages')
        .select('twilio_message_sid, read')
        .in('twilio_message_sid', externalIds);
      
      if (!messagesError && messagesData) {
        readStatusMapByExternalId = messagesData.reduce((acc, msg) => {
          if (msg.twilio_message_sid) {
            acc[msg.twilio_message_sid] = msg.read || 'unread';
          }
          return acc;
        }, {} as Record<string, 'read' | 'unread'>);
      }
    }
    
    // Map the data to include read status from all_messages
    return threadData.map((email: any) => {
      let readStatus: 'read' | 'unread' = 'unread';
      
      // Try to get read status by all_messages_id first
      if (email.all_messages_id && readStatusMapById[email.all_messages_id]) {
        readStatus = readStatusMapById[email.all_messages_id];
      }
      // Fall back to external_id lookup
      else if (email.external_id && readStatusMapByExternalId[email.external_id]) {
        readStatus = readStatusMapByExternalId[email.external_id];
      }
      
      return {
        id: email.id,
        direction: email.direction,
        subject: email.subject,
        content: email.content,
        body_html: email.body_html,
        email_address: email.email_address,
        sent_at: email.sent_at,
        received_at: email.received_at,
        created_at: email.created_at,
        external_id: email.external_id,
        all_messages_id: email.all_messages_id,
        read: readStatus
      };
    });
  } catch (error) {
    console.error('Error fetching quote request email thread:', error);
    throw error;
  }
}

/**
 * Get unread message count for a quote request group
 */
export async function getUnreadCountForQuoteRequestGroup(
  businessId: string,
  groupId: string
): Promise<number> {
  if (!supabase) throw new Error('Database connection not available');

  try {
    // Get all_messages_id values from quote_request_email_threads for this group
    const { data: threadData, error: threadError } = await supabase
      .from('quote_request_email_threads')
      .select('all_messages_id, external_id')
      .eq('business_id', businessId)
      .eq('quote_request_group_id', groupId)
      .eq('direction', 'inbound'); // Only count inbound (supplier) messages
    
    if (threadError) throw threadError;
    if (!threadData || threadData.length === 0) return 0;
    
    // Get all_messages_id values that are not null
    const allMessagesIds = threadData
      .map(email => email.all_messages_id)
      .filter((id): id is string => id !== null);
    
    // If some emails don't have all_messages_id, try to find them by external_id (gmail_id)
    if (allMessagesIds.length < threadData.length) {
      const emailsWithoutId = threadData.filter(email => !email.all_messages_id);
      const externalIds = emailsWithoutId
        .map(email => email.external_id)
        .filter((id): id is string => id !== null);
      
      if (externalIds.length > 0) {
        const { data: missingMessages, error: lookupError } = await supabase
          .from('all_messages')
          .select('id')
          .eq('business_id', businessId)
          .eq('message_type', 'email')
          .in('twilio_message_sid', externalIds);
        
        if (!lookupError && missingMessages) {
          const foundIds = missingMessages.map(msg => msg.id);
          allMessagesIds.push(...foundIds);
        }
      }
    }
    
    if (allMessagesIds.length === 0) return 0;
    
    // Count unread messages from all_messages
    const { count, error: messagesError } = await supabase
      .from('all_messages')
      .select('*', { count: 'exact', head: true })
      .in('id', allMessagesIds)
      .eq('read', 'unread');
    
    if (messagesError) throw messagesError;
    
    return count || 0;
  } catch (error) {
    console.error('Error fetching unread count for quote request group:', error);
    return 0;
  }
}
